import { useLayoutEffect, useRef } from 'react'

import Anchor from '@/components/ui/Anchor'
import Backdrop from '@/components/ui/Backdrop'
import Icon from '@/components/ui/Icon'
import Wordmark from '@/components/ui/Wordmark'
import { useData } from '@/hooks/useLocale'
import { useScrollDriver } from '@/hooks/useScrollDriver'
import { ease, GLIDE, lerp, ramp, stickyProgress } from '@/lib/scroll'

// Where the white wordmark takes over the screen. The header reads it from
// `data-light-from` and turns dark from there, as it does past the hero.
const LIGHT_FROM = 0.76

// While a layer is mid-animation, ask the browser to keep it on its own
// compositing layer, so its fades and moves never repaint the photo.
const LAYER = 'will-change-[opacity,transform]'

/**
 * The home hero is a story played by scrolling (design: Home, Hero). The
 * section is 320vh tall and its first screen sticks while the scroll progress
 * runs 0 → 1: the intro gives way to one statement per screen, each with its
 * last word marked in blue, then a giant MEETNPLAN cut out of white zooms in
 * over the photo and the closing line and buttons rise beneath it.
 *
 * React renders the stage once; on every frame `paint` moves its parts (found
 * by `data-part`) straight in the DOM, so scrolling costs no re-render.
 *
 * The stage is as tall as the visible screen (`dvh`, which follows a phone's
 * address bar as it hides), and everything behind and around it is white like
 * the story's last screen, so no dark sliver can show on a phone where it ends.
 */
export default function HeroSection() {
  const { HERO } = useData()
  const section = useRef(null)
  const stage = useRef(null)
  const parts = useRef(null)
  const progress = useRef(0)

  // After every render (the language can change the statements), find the
  // parts again and put them where the current progress has them.
  useLayoutEffect(() => {
    parts.current = findParts(stage.current)
    paint(parts.current, progress.current)
  })

  useScrollDriver(
    section,
    stickyProgress,
    (p) => {
      progress.current = p
      if (parts.current) paint(parts.current, p)
    },
    GLIDE,
  )

  return (
    <section
      ref={section}
      data-light-from={LIGHT_FROM}
      className="relative h-[320vh] bg-white text-white"
    >
      <div
        ref={stage}
        className="sticky top-0 isolate h-dvh overflow-hidden bg-ink"
      >
        <Backdrop />
        <div
          data-part="dim"
          aria-hidden="true"
          className="absolute inset-0 bg-[rgba(12,26,51,.62)]"
        />

        {/* Intro: rises in on load, lifts away as the story starts. */}
        <div data-part="intro" className="absolute inset-0">
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-[clamp(20px,6vw,80px)] text-center">
            <span className="animate-rise text-xs font-semibold tracking-[.2em] text-sky uppercase [animation-delay:.1s] motion-reduce:animate-none">
              {HERO.eyebrow}
            </span>
            <h1 className="max-w-270 animate-rise text-[clamp(44px,8vw,112px)] leading-[.98] font-extrabold tracking-[-0.04em] text-balance [animation-delay:.25s] motion-reduce:animate-none">
              {HERO.title}
            </h1>
            <p className="max-w-140 animate-rise text-[clamp(16px,1.6vw,19px)]/[1.5] text-[#dbe4f3] [animation-delay:.45s] motion-reduce:animate-none">
              {HERO.text}
            </p>
          </div>
          <div
            aria-hidden="true"
            className="absolute bottom-9 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2.5"
          >
            <span className="text-[11px] font-semibold tracking-[.2em] text-sky uppercase">
              {HERO.scroll}
            </span>
            <span className="relative h-11 w-px overflow-hidden bg-white/25">
              <span className="absolute top-0 left-0 h-[18px] w-px animate-drop bg-white motion-reduce:animate-none" />
            </span>
          </div>
        </div>

        {/* The story, one statement per stretch of scroll. */}
        {HERO.frames.map((frame) => {
          const words = frame.title.split(' ')
          return (
            <div
              key={frame.title}
              data-part="frame"
              className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-4 px-[clamp(20px,6vw,80px)] text-center"
            >
              <div className="pb-2.5">
                <h2 className="max-w-225 text-[clamp(36px,6vw,76px)] leading-[1.06] font-bold tracking-[-0.035em] text-balance">
                  {words.map((word, j) =>
                    j < words.length - 1 ? (
                      <span key={j} className="me-[.24em] inline-block">
                        {word}
                      </span>
                    ) : (
                      <span
                        key={j}
                        data-part="marker"
                        className="-mx-[.16em] inline-block rounded-[14px] bg-[linear-gradient(#1a73e8,#1a73e8)] bg-left bg-no-repeat px-[.16em] rtl:bg-right"
                      >
                        {word}
                      </span>
                    ),
                  )}
                </h2>
              </div>
              <p className="max-w-140 text-[clamp(16px,1.6vw,19px)]/[1.5] text-[#dbe4f3]">
                {frame.text}
              </p>
            </div>
          )
        })}

        {/* Progress through the statements (the last leads into the wordmark). */}
        <div
          data-part="bars"
          aria-hidden="true"
          className="absolute bottom-18 left-1/2 flex -translate-x-1/2 gap-1.5"
        >
          {HERO.frames.slice(0, -1).map((frame) => (
            <span
              key={frame.title}
              data-part="bar"
              className="h-[3px] w-8 rounded-[2px] bg-white/30 transition-colors duration-200"
            />
          ))}
        </div>

        {/* White over the photo with the wordmark cut out of it: in screen
            blending, white stays white and the black letters show the photo.
            It overhangs the stage by 1px so the stage's edge is white too. */}
        <div
          data-part="overlay"
          aria-hidden="true"
          className="pointer-events-none absolute -inset-px flex items-center justify-center bg-white mix-blend-screen"
        >
          <Wordmark className="-mt-[6vh] text-black">{HERO.wordmark}</Wordmark>
        </div>

        <div
          data-part="closing"
          className="absolute inset-x-0 top-[calc(50%+clamp(40px,9vw,130px))] z-2 flex flex-col items-center gap-[18px] text-center"
        >
          <p className="px-5 text-[17px] text-muted">{HERO.closing}</p>
          <div className="flex flex-wrap justify-center gap-2.5">
            <Anchor
              href={HERO.primary.href}
              className="flex h-12 items-center gap-3.5 rounded-full bg-brand ps-5.5 pe-2 text-sm font-semibold text-white hover:bg-brand-dark"
            >
              {HERO.primary.label}
              <span className="grid size-[34px] place-items-center rounded-full bg-white text-brand">
                <Icon name="arrowRight" size={15} stroke={2.2} />
              </span>
            </Anchor>
            <Anchor
              href={HERO.secondary.href}
              className="flex h-12 items-center rounded-full px-[22px] text-sm font-medium text-ink inset-ring inset-ring-[#dfe3ea] hover:bg-surface"
            >
              {HERO.secondary.label}
            </Anchor>
          </div>
        </div>
      </div>
    </section>
  )
}

/** The stage's moving parts, by their `data-part`. */
function findParts(stage) {
  const one = (name) => stage.querySelector(`[data-part="${name}"]`)
  const all = (name) => [...stage.querySelectorAll(`[data-part="${name}"]`)]
  return {
    dim: one('dim'),
    intro: one('intro'),
    frames: all('frame').map((el) => ({
      el,
      title: el.querySelector('h2'),
      marker: el.querySelector('[data-part="marker"]'),
      lead: el.querySelector('p'),
    })),
    bars: one('bars'),
    bar: all('bar'),
    overlay: one('overlay'),
    closing: one('closing'),
  }
}

/** Puts every part where progress `p` (0 → 1) has it. */
function paint(ui, p) {
  const intro = ease(ramp(p, 0.02, 0.08))
  const zoom = ease(ramp(p, 0.73, 0.93))
  const closing = ease(ramp(p, 0.91, 0.99))
  const animating = (t) => t > 0 && t < 1

  ui.dim.style.opacity = ramp(p, 0, 0.04)

  ui.intro.style.opacity = 1 - intro
  ui.intro.style.transform = `translateY(${-intro * 30}px)`
  ui.intro.classList.toggle('pointer-events-none', intro > 0.5)
  ui.intro.classList.toggle(LAYER, animating(intro))

  // Each statement has its own tenth of the run; the bar under the one on
  // screen lights up (the last statement leads into the wordmark, unbarred).
  let current = -1
  ui.frames.forEach((frame, i) => {
    const start = 0.07 + i * 0.1
    const u = ramp(p, start, start + 0.1)
    if (animating(u) && i < ui.frames.length - 1) current = i
    const fadeIn = ease(ramp(u, 0, 0.32))
    const fadeOut = ease(ramp(u, 0.74, 1))
    const lead = ease(ramp(u, 0.26, 0.46))
    frame.el.style.opacity = fadeIn * (1 - fadeOut)
    frame.el.classList.toggle(LAYER, animating(u))
    frame.title.style.opacity = fadeIn
    frame.marker.style.backgroundSize = `${ease(ramp(u, 0.22, 0.42)) * 100}% 100%`
    frame.lead.style.opacity = lead
    frame.lead.style.transform = `translateY(${(1 - lead) * 14}px)`
  })

  ui.bars.style.opacity = ramp(p, 0.06, 0.1) * (1 - ramp(p, 0.55, 0.59))
  ui.bar.forEach((bar, i) => {
    bar.classList.toggle('bg-white', i === current)
    bar.classList.toggle('bg-white/30', i !== current)
  })

  ui.overlay.style.opacity = ramp(p, 0.71, 0.81)
  ui.overlay.style.transform = `scale(${lerp(6, 1, zoom)})`
  ui.overlay.classList.toggle(LAYER, p > 0.7 && p < 0.94)

  ui.closing.style.opacity = closing
  ui.closing.style.transform = `translateY(${(1 - closing) * 20}px)`
  ui.closing.inert = closing < 0.5
  ui.closing.classList.toggle('pointer-events-none', closing <= 0.5)
  ui.closing.classList.toggle(LAYER, animating(closing))
}
